import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { rawFetch } from "@/lib/api/client";
import { PageHeader, ErrorBox, StatusPill, DataTable } from "@/components/common";
import { useApp } from "@/lib/store";
import { fmtDate, shortId } from "@/lib/utils";
import { WorkspaceJourney } from "@/components/WorkspaceJourney";

interface AgentRun { id: string; agent: string; status: string; created_at: string; finished_at?: string | null }
interface PipelineRun { id: string; pipeline: string; status: string; started_at?: string | null }
interface Paged<T> { count: number; results: T[] }

function resolveBackendHealthUrl(): string {
  const apiBase = (import.meta.env.VITE_API_BASE_URL || "/api/v1").trim();
  const explicitRoot = (import.meta.env.VITE_BACKEND_BASE_URL || "").trim();
  if (explicitRoot) return `${explicitRoot.replace(/\/+$/, "")}/healthz`;

  if (/^https?:\/\//i.test(apiBase)) {
    return `${apiBase.replace(/\/api\/v1\/?$/i, "").replace(/\/+$/, "")}/healthz`;
  }

  if (typeof window !== "undefined" && window.location.host.startsWith("ontoligentfe.")) {
    const beHost = window.location.host.replace("ontoligentfe.", "ontoligentbe.");
    return `${window.location.protocol}//${beHost}/healthz`;
  }

  return "/healthz";
}

export function Dashboard() {
  const ws = useApp((s) => s.workspaceId);
  const healthUrl = resolveBackendHealthUrl();
  const health = useQuery({
    queryKey: ["health"],
    queryFn: () => fetch(healthUrl).then((r) => r.json()),
  });
  const wsq = ws ? `&workspace=${ws}` : "";
  const runs = useQuery<Paged<AgentRun>>({
    queryKey: ["dash", "agent-runs", ws],
    queryFn: () => rawFetch(`/agents/runs/?ordering=-created_at&page_size=10${wsq}`),
  });
  const pipes = useQuery<Paged<PipelineRun>>({
    queryKey: ["dash", "pipeline-runs", ws],
    queryFn: () => rawFetch(`/pipelines/runs/?ordering=-started_at&page_size=10${wsq}`),
  });
  // Lightweight totals — we just need .count from a 1-row page.
  const agents = useQuery<Paged<unknown>>({
    queryKey: ["dash", "agents-count", ws],
    queryFn: () => rawFetch(`/agents/agents/?page_size=1${wsq}`),
  });
  const sources = useQuery<Paged<unknown>>({
    queryKey: ["dash", "sources-count", ws],
    queryFn: () => rawFetch(`/catalog/sources/?page_size=1${wsq}`),
  });
  const ingest = useQuery<Paged<unknown>>({
    queryKey: ["dash", "ingest-count", ws],
    queryFn: () => rawFetch(`/catalog/runs/?page_size=1${wsq}`),
  });
  const pipelinesCount = useQuery<Paged<unknown>>({
    queryKey: ["dash", "pipelines-count", ws],
    queryFn: () => rawFetch(`/pipelines/pipelines/?page_size=1${wsq}`),
  });
  const datasets = useQuery<Paged<unknown>>({
    queryKey: ["dash", "datasets-count", ws],
    queryFn: () => rawFetch(`/catalog/datasets/?page_size=1${wsq}`),
  });
  const ontologyVersions = useQuery<Paged<unknown>>({
    queryKey: ["dash", "ontology-count", ws],
    queryFn: () => rawFetch(`/ontology/versions/?page_size=1${wsq}`),
  });
  const retrievalProfiles = useQuery<Paged<unknown>>({
    queryKey: ["dash", "retrieval-count", ws],
    queryFn: () => rawFetch(`/retrieval/profiles/?page_size=1${wsq}`),
  });
  const evalSets = useQuery<Paged<unknown>>({
    queryKey: ["dash", "evals-count", ws],
    queryFn: () => rawFetch(`/evals/datasets/?page_size=1${wsq}`),
  });

  // Derived KPI: health of the most recent runs (agent + pipeline).
  const recentRuns = [
    ...(runs.data?.results ?? []).map((r) => r.status),
    ...(pipes.data?.results ?? []).map((r) => r.status),
  ];
  const okStatuses = new Set(["succeeded", "success", "completed", "complete", "ok", "done"]);
  const failStatuses = new Set(["failed", "error", "errored", "cancelled", "canceled"]);
  const finished = recentRuns.filter((s) => okStatuses.has(s) || failStatuses.has(s));
  const okCount = finished.filter((s) => okStatuses.has(s)).length;
  const successRate = finished.length ? Math.round((okCount / finished.length) * 100) : null;

  return (
    <>
      <PageHeader
        title="Dashboard"
        subtitle="Health, totals, and recent runs for this workspace."
        help={
          <>
            <p>Every tile and table is scoped to the selected workspace. Totals cover catalog sources, pipelines, agents and the knowledge graph; the tables show the latest agent and pipeline runs with status and timing.</p>
          </>
        }
        learnMore={{ to: "/howto", hash: "dashboard", label: "Reading the dashboard" }}
      />
      <WorkspaceJourney />
      <div className="mb-4 grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
        <Tile label="API health" value={
          health.isLoading ? "…"
            : health.data?.status === "ok" ? <StatusPill status="ok" />
            : <StatusPill status="error" />
        } />
        <Tile label="Run success" value={
          successRate === null ? "—"
            : <span className={successRate >= 90 ? "text-good" : successRate >= 70 ? "text-warn" : "text-bad"}>{successRate}%</span>
        } hint={successRate === null ? "No finished runs yet" : `${okCount}/${finished.length} recent runs`} />
        <Tile label="Ontology versions" value={ontologyVersions.data?.count ?? "—"} to="/ontology" />
        <Tile label="Data sources"  value={sources.data?.count ?? "—"}       to="/catalog" />
        <Tile label="Datasets"      value={datasets.data?.count ?? "—"}      to="/catalog" />
        <Tile label="Pipelines"     value={pipelinesCount.data?.count ?? "—"} to="/pipelines" />
        <Tile label="Pipeline runs" value={pipes.data?.count ?? "—"}        to="/pipelines" />
        <Tile label="Agents"        value={agents.data?.count ?? "—"}        to="/agents" />
        <Tile label="Agent runs"    value={runs.data?.count ?? "—"}          to="/agents" />
        <Tile label="Retrieval profiles" value={retrievalProfiles.data?.count ?? "—"} to="/retrieval" />
        <Tile label="Eval sets"     value={evalSets.data?.count ?? "—"}     to="/evals" />
        <Tile label="Ingestion runs" value={ingest.data?.count ?? "—"}     to="/catalog" />
      </div>

      <h2 className="mb-2 text-sm font-medium text-muted">Agent runs</h2>
      {runs.isError && <ErrorBox error={runs.error} />}
      <DataTable
        rows={runs.data?.results ?? []}
        cols={[
          { header: "ID",      cell: (r) => <code className="font-mono text-xs">{shortId(r.id)}</code> },
          { header: "Agent",   cell: (r) => <code className="font-mono text-xs">{shortId(r.agent)}</code> },
          { header: "Status",  cell: (r) => <StatusPill status={r.status} /> },
          { header: "Created", cell: (r) => fmtDate(r.created_at) },
          { header: "Finished",cell: (r) => fmtDate(r.finished_at) },
        ]}
        empty="No agent runs yet — trigger one from the Agents page."
      />

      <h2 className="mb-2 mt-6 text-sm font-medium text-muted">Pipeline runs</h2>
      {pipes.isError && <ErrorBox error={pipes.error} />}
      <DataTable
        rows={pipes.data?.results ?? []}
        cols={[
          { header: "ID",      cell: (r) => <code className="font-mono text-xs">{shortId(r.id)}</code> },
          { header: "Pipeline",cell: (r) => <code className="font-mono text-xs">{shortId(r.pipeline)}</code> },
          { header: "Status",  cell: (r) => <StatusPill status={r.status} /> },
          { header: "Started", cell: (r) => fmtDate(r.started_at) },
        ]}
        empty="No pipeline runs yet."
      />
    </>
  );
}

function Tile({ label, value, to, hint }: { label: string; value: React.ReactNode; to?: string; hint?: string }) {
  const body = (
    <>
      <div className="text-xs uppercase text-muted">{label}</div>
      <div className="mt-2 text-2xl font-semibold">{value}</div>
      {hint && <div className="mt-1 text-[10px] text-muted">{hint}</div>}
    </>
  );
  return to ? (
    <Link to={to} className="card transition hover:border-accent/50">{body}</Link>
  ) : (
    <div className="card">{body}</div>
  );
}
